// Poll authoring and results logic. Dependency-free and DOM-free so it can be
// unit-tested under bun; the poll views stay thin renderers over it.
//
// The editor works on a *draft*: the poll in editable shape, with each
// question's options as one-per-line text. `pollToDraft` makes one from a poll
// as d3-community returns it, and `buildPollPayload` turns one back into the
// write shape (plain option labels, no ids).

/** d3-community's limits, mirrored so the form can say so before a round trip. */
export const TITLE_MAX = 200;
export const INTRO_MAX = 20000;
export const PROMPT_MAX = 5000;
export const OPTION_MAX = 200;
export const MAX_QUESTIONS = 50;
export const MAX_OPTIONS = 50;

/** The results-visibility settings, in the order the editor offers them. */
export const RESULTS_VISIBILITIES = [
    {
        value: 'after_voting',
        label: 'After voting',
        help: 'A respondent sees the tallies as soon as they submit.',
    },
    {
        value: 'at_close',
        label: 'At close',
        help: 'Nobody sees results until the poll closes; respondents are notified then.',
    },
];

/** Bootstrap badge class per poll state. */
export const STATE_BADGE = {
    draft: 'text-bg-warning',
    open: 'text-bg-success',
    closed: 'text-bg-secondary',
};

/** @returns {{kind: string, prompt: string, allowMultiple: boolean, allowNotListed: boolean, options: string}} */
export function newQuestion(kind = 'choice') {
    return { kind, prompt: '', allowMultiple: false, allowNotListed: false, options: '' };
}

/** The draft for a poll that does not exist yet. */
export function emptyDraft() {
    return {
        title: '',
        intro: '',
        resultsVisibility: 'after_voting',
        allowChanges: false,
        closesAt: '',
        questions: [],
    };
}

/**
 * A poll as d3-community returns it, in editable shape. Option labels become
 * one-per-line text; ids are dropped (d3-community regenerates them on save).
 * @param {object} poll
 */
export function pollToDraft(poll) {
    return {
        title: poll.title,
        intro: poll.intro ?? '',
        resultsVisibility: poll.resultsVisibility,
        allowChanges: Boolean(poll.allowChanges),
        closesAt: isoToLocalInput(poll.closesAt),
        questions: (poll.questions ?? []).map((q) => ({
            kind: q.kind,
            prompt: q.prompt,
            allowMultiple: Boolean(q.allowMultiple),
            allowNotListed: Boolean(q.allowNotListed),
            options: (q.options ?? []).map((o) => o.label).join('\n'),
        })),
    };
}

/**
 * One-per-line option text as labels, trimmed, blank lines dropped.
 * @param {string} text
 * @returns {string[]}
 */
export function splitOptions(text) {
    return text
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');
}

/**
 * A draft's questions in the shape the preview renders — the read shape, with
 * positional ids so the preview's inputs can be labelled.
 * @param {ReturnType<typeof emptyDraft>} draft
 */
export function draftToPreview(draft) {
    return {
        title: draft.title.trim(),
        intro: draft.intro.trim(),
        resultsVisibility: draft.resultsVisibility,
        allowChanges: draft.allowChanges,
        closesAt: localInputToIso(draft.closesAt) || undefined,
        questions: draft.questions.map((q, i) => ({
            id: `q${i}`,
            kind: q.kind,
            prompt: q.prompt.trim(),
            allowMultiple: q.kind === 'choice' && q.allowMultiple,
            allowNotListed: q.kind === 'choice' && q.allowNotListed,
            options:
                q.kind === 'choice'
                    ? splitOptions(q.options).map((label, j) => ({ id: `q${i}o${j}`, label }))
                    : [],
        })),
    };
}

/**
 * Validate and shape a draft into a request body, collecting every problem in
 * field order. A draft may be incomplete (no questions, a choice question with
 * one option) — d3-community checks readiness when the poll opens, not here.
 * Text questions drop their choice-only fields: d3-community refuses a text
 * question that carries options.
 * @param {ReturnType<typeof emptyDraft>} draft
 * @param {{now?: number}} [options] epoch millis, for the close-time check
 * @returns {{payload: object|null, errors: string[]}}
 */
export function buildPollPayload(draft, { now = Date.now() } = {}) {
    const errors = [];
    const title = draft.title.trim();
    const intro = draft.intro.trim();

    if (!title) errors.push('Title is required.');
    if (title.length > TITLE_MAX) errors.push(`Title must be ${TITLE_MAX} characters or fewer.`);
    if (intro.length > INTRO_MAX) {
        errors.push(`Introduction must be ${INTRO_MAX} characters or fewer.`);
    }
    if (!RESULTS_VISIBILITIES.some((v) => v.value === draft.resultsVisibility)) {
        errors.push('Pick when results are shown.');
    }

    let closesAt;
    if (draft.closesAt) {
        const iso = localInputToIso(draft.closesAt);
        if (!iso) errors.push('The close time is not a valid date and time.');
        else if (Date.parse(iso) <= now) errors.push('The close time has already passed.');
        else closesAt = iso;
    }

    if (draft.questions.length > MAX_QUESTIONS) {
        errors.push(`A poll can have at most ${MAX_QUESTIONS} questions.`);
    }
    const questions = draft.questions.map((q, i) => {
        const n = i + 1;
        const prompt = q.prompt.trim();
        if (!prompt) errors.push(`Question ${n} needs a prompt.`);
        if (prompt.length > PROMPT_MAX) {
            errors.push(`Question ${n}'s prompt must be ${PROMPT_MAX} characters or fewer.`);
        }
        if (q.kind === 'text') return { kind: 'text', prompt };

        const options = splitOptions(q.options);
        if (options.length > MAX_OPTIONS) {
            errors.push(`Question ${n} can have at most ${MAX_OPTIONS} options.`);
        }
        const seen = new Set();
        for (const option of options) {
            if (option.length > OPTION_MAX) {
                errors.push(`Question ${n} has an option longer than ${OPTION_MAX} characters.`);
                break;
            }
            if (seen.has(option)) {
                errors.push(`Question ${n} lists "${option}" twice.`);
                break;
            }
            seen.add(option);
        }
        return {
            kind: 'choice',
            prompt,
            allowMultiple: q.allowMultiple,
            allowNotListed: q.allowNotListed,
            options,
        };
    });

    if (errors.length > 0) return { payload: null, errors };

    const payload = {
        title,
        intro,
        resultsVisibility: draft.resultsVisibility,
        allowChanges: draft.allowChanges,
        questions,
    };
    if (closesAt) payload.closesAt = closesAt;
    return { payload, errors };
}

/** A draft reduced to what gets saved, for comparison. */
function normalize(draft) {
    return JSON.stringify({
        title: draft.title.trim(),
        intro: draft.intro.trim(),
        resultsVisibility: draft.resultsVisibility,
        allowChanges: Boolean(draft.allowChanges),
        closesAt: localInputToIso(draft.closesAt),
        questions: draft.questions.map((q) => ({
            kind: q.kind,
            prompt: q.prompt.trim(),
            allowMultiple: q.kind === 'choice' && q.allowMultiple,
            allowNotListed: q.kind === 'choice' && q.allowNotListed,
            options: q.kind === 'choice' ? splitOptions(q.options) : [],
        })),
    });
}

/**
 * Whether the editor holds changes not yet saved. Compares what would be saved,
 * so surrounding whitespace and blank option lines do not count.
 * @param {object|null} saved the poll as last returned by the API; null before the first save
 * @param {ReturnType<typeof emptyDraft>} draft
 */
export function isDirty(saved, draft) {
    if (!saved) return true;
    return normalize(pollToDraft(saved)) !== normalize(draft);
}

/**
 * Move a question up or down, returning the new order, or null when the move
 * would run off either end.
 * @template T
 * @param {T[]} questions
 * @param {number} index
 * @param {number} delta
 * @returns {T[]|null}
 */
export function moveQuestion(questions, index, delta) {
    const target = index + delta;
    if (index < 0 || index >= questions.length || target < 0 || target >= questions.length) {
        return null;
    }
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
}

/**
 * Whether a poll's content (everything but the close time) may still change.
 * Questions lock when a poll opens: editing them after responses arrive would
 * corrupt the tallies.
 * @param {{state: string}|null} poll null for a poll not yet saved
 */
export function canEditContent(poll) {
    return !poll || poll.state === 'draft';
}

/**
 * Whether the close time may still change — the one edit d3-community accepts
 * on an open poll.
 * @param {{state: string}|null} poll
 */
export function canEditClosesAt(poll) {
    return !poll || poll.state !== 'closed';
}

/**
 * What closing this poll does beyond closing it, for the confirmation.
 * @param {{resultsVisibility: string}} poll
 */
export function closeConsequence(poll) {
    return poll.resultsVisibility === 'at_close'
        ? 'Closing releases the results and notifies every respondent.'
        : 'Closing stops responses; results are already visible to respondents.';
}

/**
 * An ISO timestamp as a `datetime-local` input value in the browser's zone,
 * '' when unset.
 * @param {string|null|undefined} iso
 */
export function isoToLocalInput(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
}

/**
 * A `datetime-local` value (local time) as an ISO timestamp, '' when unset or
 * unparseable.
 * @param {string} value
 */
export function localInputToIso(value) {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

/**
 * Where members answer a poll on the community portal — the link to share.
 * Only meaningful once the poll is open (the portal 404s a draft).
 * @param {string} portalUrl the portal's origin (communityPortalUrl from /config)
 * @param {string} id
 */
export function portalPollUrl(portalUrl, id) {
    if (!portalUrl) return '';
    return `${portalUrl.replace(/\/+$/, '')}/polls/${encodeURIComponent(id)}`;
}

/**
 * A whole-number percentage, 0 when there is nothing to divide by.
 * @param {number} count
 * @param {number} total
 */
export function percent(count, total) {
    if (!total || total < 1) return 0;
    return Math.round((count / total) * 100);
}

/**
 * The bars for one choice question's results: each option, then "Not listed"
 * when the question offers it. Percentages are of the respondents who answered
 * this question — the portal's arithmetic — so on a pick-any question they can
 * add up to more than 100.
 * @param {{allowNotListed?: boolean}} question
 * @param {{answered: number, notListed: number, options: Array<{optionId: string, label: string, count: number}>}} result
 * @returns {Array<{label: string, count: number, percent: number, notListed: boolean}>}
 */
export function tallyRows(question, result) {
    const rows = result.options.map((option) => ({
        label: option.label,
        count: option.count,
        percent: percent(option.count, result.answered),
        notListed: false,
    }));
    if (question.allowNotListed) {
        rows.push({
            label: 'Not listed',
            count: result.notListed,
            percent: percent(result.notListed, result.answered),
            notListed: true,
        });
    }
    return rows;
}

/**
 * A question's prompt as one line of plain text: its first line, without images
 * or Markdown punctuation. For results, where rendering the whole prompt again
 * — screenshots included — would repeat the poll.
 * @param {string} prompt
 */
export function promptSummary(prompt) {
    const firstLine = prompt.split('\n').find((line) => line.trim() !== '') ?? '';
    return firstLine
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
        .replace(/[#*_`>]/g, '')
        .trim();
}
